import React, { ReactNode } from 'react';
import { useRBACContext } from '../../context/rbac';
import { ActionType, ResourceType } from '../../types/rbac';

interface ProtectedComponentProps {
  children: ReactNode;
  resource: ResourceType;
  action: ActionType;
  fallback?: ReactNode;
  showLoading?: boolean;
}

/**
 * Componente que protege el contenido basado en permisos RBAC
 */
export const ProtectedComponent: React.FC<ProtectedComponentProps> = ({
  children,
  resource,
  action,
  fallback = null,
  showLoading = true,
}) => {
  const { hasPermission, loading } = useRBACContext();

  if (loading && showLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasPermission(resource, action)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

interface RequirePermissionsProps {
  children: ReactNode;
  permissions: { resource: ResourceType; action: ActionType }[];
  requireAll?: boolean; // true = requiere todos los permisos, false = requiere al menos uno
  fallback?: ReactNode;
  showLoading?: boolean;
}

/**
 * Componente que requiere múltiples permisos
 */
export const RequirePermissions: React.FC<RequirePermissionsProps> = ({
  children,
  permissions,
  requireAll = true,
  fallback = null,
  showLoading = true,
}) => {
  const { hasPermission, loading } = useRBACContext();

  if (loading && showLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const hasRequiredPermissions = requireAll
    ? permissions.every(({ resource, action }) =>
        hasPermission(resource, action),
      )
    : permissions.some(({ resource, action }) =>
        hasPermission(resource, action),
      );

  if (!hasRequiredPermissions) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Componente de mensaje de acceso denegado
 */
export const AccessDenied: React.FC<{ message?: string }> = ({
  message = 'No tienes permisos para acceder a esta sección',
}) => {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <strong className="font-bold">Acceso Denegado</strong>
        <span className="block sm:inline"> {message}</span>
      </div>
      <p className="text-gray-600">
        Si crees que esto es un error, contacta al administrador del sistema.
      </p>
    </div>
  );
};
